import { apiGet } from "../client";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/query-keys";
import type { IExamSection } from "../exams";
import type { IExamSet } from "../exam_sets/types";

// Types for the exam report response
export interface ISimpleUser {
  id: string;
  name: string;
  email: string;
  school?: string;
  grade?: number;
}

export interface ISATQuestion {
  question_id: string;
  user_answer: string;
  correct_answer: string;
  is_correct: boolean;
  category_id: string;
}

export interface IReportSection {
  questions: ISATQuestion[];
}

export interface ICategoryStat {
  category_id: string;
  correct_count: number;
  total_count: number;
  accuracy: number;
}

export interface ISectionDomain {
  id: string;
  name: string;
  question_ids?: string[];
  correct_answer_ids?: string[];
  total_questions?: number;
  correct_answers?: number;
  accuracy?: number;
}

export interface ISubjectReport {
  subject: string;
  sections: IReportSection[];
  total_score: number;
  difficulty?: number;
  domains: ISectionDomain[];
  category_stats: ICategoryStat[] | null;
  recent_exams_stats: ICategoryStat[] | null;
}

export interface IGroup {
  id: string;
  name: string;
}

export interface IExamReport {
  id: string;
  exam_set_id: string;
  exam_set?: IExamSet;
  exam_progress_id: string;
  user_id: string;
  user: ISimpleUser;
  group?: IGroup;
  total_score: number;
  exam_type: string;
  subjects: ISubjectReport[];
  created_at: string;
  updated_at: string;
}

// API function to get exam report
export const getExamReport = async (id: string) => {
  const response = await apiGet<IExamReport>(`/exam_progress/${id}/report`);
  return response.data;
};

// React Query hook
export const useQueryGetExamReport = (id: string) => {
  return useQuery({
    queryKey: QueryKeys.examProgress.getReport(id),
    queryFn: () => getExamReport(id),
  });
};
